/**
 * TBGenSurv Module 4 — ML Data Store
 *
 * Simulates a real ML resistance prediction pipeline.
 * Extends the HIV k-mer approach (Mhlanga 2025) to M. tuberculosis.
 *
 * Drugs covered:
 *   First-line:  Isoniazid (INH), Rifampicin (RIF),
 *                Pyrazinamide (PZA), Ethambutol (EMB)
 *   Second-line: Moxifloxacin (MFX), Bedaquiline (BDQ),
 *                Linezolid (LZD), Clofazimine (CFZ)
 *
 * Training data: CRyPTIC consortium (15,000+ isolates) +
 *                PATRIC public WGS + NMRL Zimbabwe (incoming)
 */

export const DRUGS = [
  { id: 'INH', name: 'Isoniazid',     line: 1, genes: ['katG','inhA','ahpC'],   color: '#E74C3C' },
  { id: 'RIF', name: 'Rifampicin',    line: 1, genes: ['rpoB'],                  color: '#E67E22' },
  { id: 'PZA', name: 'Pyrazinamide',  line: 1, genes: ['pncA','rpsA'],           color: '#F1C40F' },
  { id: 'EMB', name: 'Ethambutol',    line: 1, genes: ['embB','embA','embC'],    color: '#2ECC71' },
  { id: 'MFX', name: 'Moxifloxacin', line: 2, genes: ['gyrA','gyrB'],           color: '#3498DB' },
  { id: 'BDQ', name: 'Bedaquiline',   line: 2, genes: ['atpE','Rv0678','pepQ'], color: '#9B59B6' },
  { id: 'LZD', name: 'Linezolid',     line: 2, genes: ['rrl','rplC'],            color: '#1ABC9C' },
  { id: 'CFZ', name: 'Clofazimine',   line: 2, genes: ['Rv0678','pepQ'],         color: '#E91E63' },
];

// ── MODEL REGISTRY ────────────────────────────────────────────────────
export const MODEL_REGISTRY = [
  {
    id: 'mdl-001',
    name: 'TBGenSurv-kmer-XGBoost-v1.0',
    version: '1.0.0',
    status: 'production',
    created: '2026-03-10T09:00:00Z',
    description: 'k-mer (k=6) feature extraction + XGBoost ensemble. Primary production model. Extended from HIV-1 NRTI resistance pipeline (Mhlanga 2025).',
    method: 'k-mer + XGBoost',
    kmer_size: 6,
    feature_count: 8192,
    training_samples: 12847,
    test_samples: 3212,
    training_source: 'CRyPTIC Consortium + PATRIC',
    overall_accuracy: 0.974,
    overall_auc: 0.991,
    per_drug: {
      INH: { accuracy: 0.971, auc: 0.989, sensitivity: 0.968, specificity: 0.974, f1: 0.970, mcc: 0.942 },
      RIF: { accuracy: 0.983, auc: 0.997, sensitivity: 0.981, specificity: 0.984, f1: 0.982, mcc: 0.965 },
      PZA: { accuracy: 0.934, auc: 0.962, sensitivity: 0.901, specificity: 0.956, f1: 0.927, mcc: 0.862 },
      EMB: { accuracy: 0.943, auc: 0.971, sensitivity: 0.921, specificity: 0.958, f1: 0.938, mcc: 0.881 },
      MFX: { accuracy: 0.967, auc: 0.988, sensitivity: 0.954, specificity: 0.978, f1: 0.965, mcc: 0.932 },
      BDQ: { accuracy: 0.956, auc: 0.981, sensitivity: 0.934, specificity: 0.971, f1: 0.951, mcc: 0.909 },
      LZD: { accuracy: 0.961, auc: 0.984, sensitivity: 0.948, specificity: 0.972, f1: 0.958, mcc: 0.921 },
      CFZ: { accuracy: 0.948, auc: 0.976, sensitivity: 0.927, specificity: 0.963, f1: 0.944, mcc: 0.892 },
    },
    confusion: {
      INH: { tp: 1842, tn: 2104, fp: 57, fn: 60 },
      RIF: { tp: 1614, tn: 2487, fp: 39, fn: 31 },
      PZA: { tp: 987,  tn: 2764, fp: 121, fn: 110 },
      EMB: { tp: 1124, tn: 2631, fp: 114, fn: 103 },
    },
  },
  {
    id: 'mdl-002',
    name: 'TBGenSurv-kmer-RandomForest-v1.0',
    version: '1.0.0',
    status: 'archived',
    created: '2026-02-20T09:00:00Z',
    description: 'k-mer (k=6) + Random Forest. Predecessor to v1.0 XGBoost model. Lower performance on PZA.',
    method: 'k-mer + Random Forest',
    kmer_size: 6,
    feature_count: 8192,
    training_samples: 12847,
    test_samples: 3212,
    training_source: 'CRyPTIC Consortium + PATRIC',
    overall_accuracy: 0.961,
    overall_auc: 0.982,
    per_drug: {
      INH: { accuracy: 0.958, auc: 0.981, sensitivity: 0.952, specificity: 0.963, f1: 0.957, mcc: 0.921 },
      RIF: { accuracy: 0.971, auc: 0.993, sensitivity: 0.968, specificity: 0.974, f1: 0.970, mcc: 0.944 },
      PZA: { accuracy: 0.912, auc: 0.941, sensitivity: 0.878, specificity: 0.938, f1: 0.906, mcc: 0.824 },
      EMB: { accuracy: 0.931, auc: 0.958, sensitivity: 0.908, specificity: 0.948, f1: 0.927, mcc: 0.858 },
      MFX: { accuracy: 0.951, auc: 0.976, sensitivity: 0.938, specificity: 0.962, f1: 0.948, mcc: 0.901 },
      BDQ: { accuracy: 0.941, auc: 0.968, sensitivity: 0.918, specificity: 0.958, f1: 0.937, mcc: 0.878 },
      LZD: { accuracy: 0.947, auc: 0.972, sensitivity: 0.931, specificity: 0.960, f1: 0.944, mcc: 0.892 },
      CFZ: { accuracy: 0.934, auc: 0.961, sensitivity: 0.912, specificity: 0.950, f1: 0.930, mcc: 0.863 },
    },
    confusion: {
      INH: { tp: 1812, tn: 2079, fp: 82, fn: 90 },
      RIF: { tp: 1589, tn: 2461, fp: 64, fn: 57 },
      PZA: { tp: 961,  tn: 2728, fp: 147, fn: 136 },
      EMB: { tp: 1099, tn: 2608, fp: 139, fn: 126 },
    },
  },
  {
    id: 'mdl-003',
    name: 'TBGenSurv-SNP-XGBoost-v1.0',
    version: '1.0.0',
    status: 'experimental',
    created: '2026-03-15T09:00:00Z',
    description: 'SNP-only features (no k-mer) + XGBoost. Tests whether k-mer adds value over direct SNP annotation.',
    method: 'SNP features + XGBoost',
    kmer_size: null,
    feature_count: 1247,
    training_samples: 12847,
    test_samples: 3212,
    training_source: 'CRyPTIC Consortium + PATRIC',
    overall_accuracy: 0.948,
    overall_auc: 0.971,
    per_drug: {
      INH: { accuracy: 0.944, auc: 0.968, sensitivity: 0.938, specificity: 0.949, f1: 0.943, mcc: 0.888 },
      RIF: { accuracy: 0.962, auc: 0.986, sensitivity: 0.959, specificity: 0.964, f1: 0.961, mcc: 0.924 },
      PZA: { accuracy: 0.891, auc: 0.921, sensitivity: 0.854, specificity: 0.921, f1: 0.884, mcc: 0.779 },
      EMB: { accuracy: 0.918, auc: 0.943, sensitivity: 0.891, specificity: 0.934, f1: 0.911, mcc: 0.829 },
      MFX: { accuracy: 0.938, auc: 0.962, sensitivity: 0.921, specificity: 0.950, f1: 0.934, mcc: 0.873 },
      BDQ: { accuracy: 0.924, auc: 0.952, sensitivity: 0.901, specificity: 0.941, f1: 0.919, mcc: 0.844 },
      LZD: { accuracy: 0.931, auc: 0.957, sensitivity: 0.914, specificity: 0.944, f1: 0.927, mcc: 0.859 },
      CFZ: { accuracy: 0.918, auc: 0.944, sensitivity: 0.894, specificity: 0.936, f1: 0.913, mcc: 0.832 },
    },
    confusion: {
      INH: { tp: 1784, tn: 2048, fp: 113, fn: 118 },
      RIF: { tp: 1572, tn: 2438, fp: 87, fn: 74 },
      PZA: { tp: 934,  tn: 2690, fp: 175, fn: 163 },
      EMB: { tp: 1073, tn: 2581, fp: 166, fn: 152 },
    },
  },
];

// ── FEATURE IMPORTANCE (top k-mer features per drug) ──────────────────
export const FEATURE_IMPORTANCE = {
  INH: [
    { feature: 'AGCACC (katG 315)', importance: 0.187, gene: 'katG',  known: true,  mutation: 'S315T'   },
    { feature: 'CTGTCA (inhA -15)', importance: 0.142, gene: 'inhA',  known: true,  mutation: 'c-15t'   },
    { feature: 'GCCTGA (ahpC -46)', importance: 0.089, gene: 'ahpC',  known: true,  mutation: 'c-46a'   },
    { feature: 'TACCGC (katG 209)', importance: 0.071, gene: 'katG',  known: false, mutation: 'Novel?'  },
    { feature: 'AAGCGT (inhA  64)', importance: 0.058, gene: 'inhA',  known: false, mutation: 'Novel?'  },
    { feature: 'CCGATG (kasA 269)', importance: 0.047, gene: 'kasA',  known: true,  mutation: 'A269V'   },
    { feature: 'GTCAAC (fabG1 -8)', importance: 0.039, gene: 'fabG1', known: true,  mutation: 'c-8t'    },
    { feature: 'TCGCCA (ndh  313)', importance: 0.031, gene: 'ndh',   known: false, mutation: 'Novel?'  },
  ],
  RIF: [
    { feature: 'TCGTTG (rpoB 450)', importance: 0.312, gene: 'rpoB',  known: true,  mutation: 'S450L'   },
    { feature: 'GGCACG (rpoB 445)', importance: 0.198, gene: 'rpoB',  known: true,  mutation: 'H445Y'   },
    { feature: 'CACCGG (rpoB 435)', importance: 0.134, gene: 'rpoB',  known: true,  mutation: 'D435V'   },
    { feature: 'AGCTGC (rpoB 516)', importance: 0.087, gene: 'rpoB',  known: true,  mutation: 'D516G'   },
    { feature: 'TTACGA (rpoB 522)', importance: 0.061, gene: 'rpoB',  known: true,  mutation: 'S522L'   },
    { feature: 'GCATCC (rpoC 516)', importance: 0.042, gene: 'rpoC',  known: false, mutation: 'Novel?'  },
    { feature: 'TGCCTG (rpoB 491)', importance: 0.031, gene: 'rpoB',  known: false, mutation: 'Rare'    },
    { feature: 'AACCGT (rpoA  12)', importance: 0.018, gene: 'rpoA',  known: false, mutation: 'Novel?'  },
  ],
};

// ── PREDICTION RESULTS ────────────────────────────────────────────────
export const PREDICTIONS = [
  {
    id: 'pred-001',
    sample_code: 'TBS-HAR-20260310-0039',
    job_id: 'wgs-001',
    model_id: 'mdl-001',
    predicted_at: '2026-03-16T11:06:51Z',
    input_type: 'WGS k-mer features',
    results: [
      { drug: 'INH', drug_name: 'Isoniazid',    prediction: 'Resistant',   prob: 0.994, dst_result: 'Resistant',   concordant: true  },
      { drug: 'RIF', drug_name: 'Rifampicin',   prediction: 'Resistant',   prob: 0.997, dst_result: 'Resistant',   concordant: true  },
      { drug: 'PZA', drug_name: 'Pyrazinamide', prediction: 'Susceptible', prob: 0.881, dst_result: 'Susceptible', concordant: true  },
      { drug: 'EMB', drug_name: 'Ethambutol',   prediction: 'Resistant',   prob: 0.743, dst_result: 'Resistant',   concordant: true  },
      { drug: 'MFX', drug_name: 'Moxifloxacin', prediction: 'Resistant',  prob: 0.912, dst_result: 'Resistant',   concordant: true  },
      { drug: 'BDQ', drug_name: 'Bedaquiline',  prediction: 'Susceptible', prob: 0.801, dst_result: null,          concordant: null  },
      { drug: 'LZD', drug_name: 'Linezolid',    prediction: 'Susceptible', prob: 0.876, dst_result: null,          concordant: null  },
      { drug: 'CFZ', drug_name: 'Clofazimine',  prediction: 'Susceptible', prob: 0.834, dst_result: null,          concordant: null  },
    ],
    classification: 'Pre-XDR-TB',
    key_mutations: ['rpoB S450L', 'katG S315T', 'inhA c-15t', 'gyrA D94G'],
    novel_signals: ['Rv2752c variant', 'rpoC secondary mutation'],
  },
];

// ── TRAINING HISTORY ──────────────────────────────────────────────────
export const TRAINING_HISTORY = [
  { epoch: 1,  loss: 0.621, val_loss: 0.634, accuracy: 0.721, val_accuracy: 0.708 },
  { epoch: 5,  loss: 0.412, val_loss: 0.428, accuracy: 0.834, val_accuracy: 0.821 },
  { epoch: 10, loss: 0.287, val_loss: 0.301, accuracy: 0.892, val_accuracy: 0.881 },
  { epoch: 20, loss: 0.198, val_loss: 0.214, accuracy: 0.931, val_accuracy: 0.924 },
  { epoch: 30, loss: 0.152, val_loss: 0.171, accuracy: 0.951, val_accuracy: 0.943 },
  { epoch: 40, loss: 0.121, val_loss: 0.144, accuracy: 0.963, val_accuracy: 0.957 },
  { epoch: 50, loss: 0.098, val_loss: 0.128, accuracy: 0.971, val_accuracy: 0.964 },
  { epoch: 60, loss: 0.081, val_loss: 0.118, accuracy: 0.976, val_accuracy: 0.968 },
  { epoch: 70, loss: 0.069, val_loss: 0.112, accuracy: 0.979, val_accuracy: 0.971 },
  { epoch: 80, loss: 0.058, val_loss: 0.109, accuracy: 0.982, val_accuracy: 0.973 },
  { epoch: 90, loss: 0.049, val_loss: 0.107, accuracy: 0.984, val_accuracy: 0.974 },
  { epoch: 100,loss: 0.042, val_loss: 0.106, accuracy: 0.986, val_accuracy: 0.974 },
];

// ── DATASET STATS ─────────────────────────────────────────────────────
export const DATASET_STATS = {
  total_isolates:   16059,
  training:         12847,
  validation:       1606,
  test:             3212,  
  mdr_tb:           3241,
  pre_xdr:          812,
  xdr_tb:           198,
  ds_tb:            11808,
  sources: [
    { name: 'CRyPTIC Consortium',    n: 10547, pct: 65.7 },
    { name: 'PATRIC / BV-BRC',       n: 3891,  pct: 24.2 },
    { name: 'NCBI SRA public',       n: 1621,  pct: 10.1 },
    { name: 'NMRL Zimbabwe (pilot)', n: 0,     pct: 0     },
  ],
  lineages: [
    { name: 'L4 Euro-American', n: 6742, pct: 42.0 },
    { name: 'L2 East Asian',    n: 3852, pct: 24.0 },
    { name: 'L3 Delhi/CAS',     n: 2249, pct: 14.0 },
    { name: 'L1 Indo-Oceanic',  n: 1766, pct: 11.0 },
    { name: 'Other',            n: 1450, pct: 9.0  },
  ],
};

// ── BENCHMARK vs TBProfiler ───────────────────────────────────────────
export const BENCHMARK = {
  INH: { tbprofiler: 0.941, who_catalogue: 0.958, tbgensurv: 0.971 },
  RIF: { tbprofiler: 0.971, who_catalogue: 0.974, tbgensurv: 0.983 },
  PZA: { tbprofiler: 0.881, who_catalogue: 0.891, tbgensurv: 0.934 },
  EMB: { tbprofiler: 0.912, who_catalogue: 0.921, tbgensurv: 0.943 },
  MFX: { tbprofiler: 0.934, who_catalogue: 0.948, tbgensurv: 0.967 },
};
